import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useCandidatosAdiantamento, useGerarAdiantamentos,
  useFechamentos, useGerarFechamentos,
  useAdicionaisPorCompetencia,
  usePendenciasFechamento,
} from "@/hooks/use-equipe";
import { formatarBRL, paraCentavos } from "@shared/equipe-fechamento";
import { BlocoPendencias } from "./mes/BlocoPendencias";
import { LinhaFechamento } from "./mes/LinhaFechamento";
import { msgDoErro } from "./mes/msg-do-erro";

// ═══════════════════════════════════════════════════════════════════════════
// Gerar adiantamentos da quinzena
// ═══════════════════════════════════════════════════════════════════════════

/**
 * A lista já chega filtrada pelo servidor (I13: só quem recebe QUINZENAL, e só
 * quem ainda não tem adiantamento no mês) e com o valor sugerido calculado LÁ.
 * O gestor pode editar o valor antes de confirmar; depois congela.
 */
export function GerarAdiantamentosDialog({
  open, onOpenChange, quinzenaId,
}: { open: boolean; onOpenChange: (v: boolean) => void; quinzenaId: string | undefined }) {
  const { data, isLoading, error } = useCandidatosAdiantamento(quinzenaId, open);
  const gerar = useGerarAdiantamentos();
  const { toast } = useToast();

  const [valores, setValores] = useState<Record<string, string>>({});
  const [competencia, setCompetencia] = useState("");

  useEffect(() => {
    if (!data) return;
    const iniciais: Record<string, string> = {};
    for (const c of data.candidatos) iniciais[c.funcionarioId] = c.valorSugerido;
    setValores(iniciais);
    setCompetencia(data.competencia);
  }, [data]);

  const confirmar = async () => {
    if (!quinzenaId || !data) return;
    try {
      await gerar.mutateAsync({
        quinzenaId,
        competencia,
        itens: data.candidatos.map(c => ({ funcionarioId: c.funcionarioId, valor: valores[c.funcionarioId] ?? c.valorSugerido })),
      });
      toast({ title: "Adiantamentos gerados!" });
      onOpenChange(false);
    } catch (e) {
      toast({ title: msgDoErro(e, "Erro ao gerar adiantamentos"), variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Adiantamento da quinzena</DialogTitle></DialogHeader>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Carregando...</p>
        ) : error ? (
          <div className="flex gap-2 rounded-lg border border-atencao/30 bg-atencao-suave p-3 text-xs text-atencao">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{msgDoErro(error, "Não foi possível carregar os candidatos.")}</span>
          </div>
        ) : !data || data.candidatos.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Ninguém a adiantar nesta competência — ou todos já têm adiantamento no mês.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Competência</Label>
              {/* Preenchida com o mês da data de pagamento (D11) e EDITÁVEL —
                  nunca inferida em silêncio. É o mês em que o valor vai abater. */}
              <Input value={competencia} onChange={e => setCompetencia(e.target.value)} placeholder="2026-08" />
              <p className="text-[11px] text-muted-foreground">
                Mês em que este adiantamento abate no fechamento. Vem da data de pagamento da quinzena.
              </p>
            </div>

            {data.candidatos.map(c => (
              <div key={c.funcionarioId} className="rounded-lg border p-2.5 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium">{c.apelido ?? c.nome}</p>
                  <Badge variant="outline" className="text-[10px]">
                    {c.adiantamentoPct}% de {formatarBRL(c.salarioMensal)}
                  </Badge>
                </div>
                <Input
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={valores[c.funcionarioId] ?? c.valorSugerido}
                  onChange={e => setValores(v => ({ ...v, [c.funcionarioId]: e.target.value }))}
                />
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={confirmar}
            disabled={!data || data.candidatos.length === 0 || gerar.isPending || !competencia}
          >
            {gerar.isPending ? "Gerando..." : "Gerar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Fechamento mensal
//
// Financeiro v3 (Ciclo 1): `BlocoPendencias`, `RegistrarPagoPorForaDialog` e
// `LinhaFechamento` MUDARAM para `components/equipe/mes/` — são da aba
// "Mensalistas · mês". Este diálogo continua funcionando com eles importados
// de lá até o Ciclo 2 apagá-lo (D-M7).
// ═══════════════════════════════════════════════════════════════════════════

export function FechamentoMensalDialog({
  open, onOpenChange, competenciaInicial,
}: { open: boolean; onOpenChange: (v: boolean) => void; competenciaInicial: string }) {
  const [competencia, setCompetencia] = useState(competenciaInicial);
  const { data: fechamentos = [], isLoading } = useFechamentos(open ? competencia : undefined);
  const { data: adicionaisComp = [] } = useAdicionaisPorCompetencia(open ? competencia : undefined);
  // Carregar a prévia RECONCILIA no servidor (terceiro caminho, só quinzena
  // ABERTA) — o que faltava no #129 e deixou agosto passar sem adiantamento.
  const { data: pendenciasData } = usePendenciasFechamento(open ? competencia : undefined);
  const gerar = useGerarFechamentos();
  const { toast } = useToast();

  const pendencias = pendenciasData?.pendencias ?? [];
  const idsPendentes = new Set(pendencias.map(p => p.funcionarioId));

  // Uma requisição para a competência inteira, não uma por linha.
  const adicionalPorFuncCentavos = new Map(
    adicionaisComp.map(a => [a.funcionario_id, paraCentavos(a.total)]),
  );

  useEffect(() => { if (open) setCompetencia(competenciaInicial); }, [open, competenciaInicial]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Fechamento mensal</DialogTitle></DialogHeader>

        <div className="space-y-1">
          <Label>Competência</Label>
          <Input value={competencia} onChange={e => setCompetencia(e.target.value)} placeholder="2026-08" />
        </div>

        {/* Antes da lista, sempre: é a pendência que decide se o mês pode fechar. */}
        <BlocoPendencias
          pendencias={pendencias}
          competencia={competencia}
          quinzenaAberta={pendenciasData?.quinzenaAberta ?? false}
        />

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Carregando...</p>
        ) : fechamentos.length === 0 ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Nenhum fechamento gerado para {competencia}.
            </p>
            <Button
              size="sm"
              disabled={gerar.isPending}
              onClick={async () => {
                try {
                  await gerar.mutateAsync(competencia);
                  toast({ title: "Fechamentos gerados." });
                } catch (e) {
                  toast({ title: msgDoErro(e, "Erro ao gerar fechamentos"), variant: "destructive" });
                }
              }}
            >
              {gerar.isPending ? "Gerando..." : "Gerar fechamentos do mês"}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {fechamentos.map(f => (
              <LinhaFechamento
                key={f.id}
                f={f}
                adicionalCentavos={adicionalPorFuncCentavos.get(f.funcionarioId) ?? 0}
                bloqueado={idsPendentes.has(f.funcionarioId)}
              />
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
